/**
 * オフライン状態・未送信回答の告知バナー。design §4.7
 */
import React, { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { getPendingAnswerCount } from "../../../offline/answerQueue";
import {
  isNetworkConnected,
  subscribeNetworkStatus,
} from "../../../native/networkStatus";

const PENDING_COUNT_POLL_MS = 5000;

export const OfflineBanner: React.FC = () => {
  // 判定できるまではオンライン扱い。誤って「オフラインです」と出す方が実害が大きい
  const [isOnline, setIsOnline] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // 接続状態の正は OS（@capacitor/network）のみ。window の online/offline は
    // 購読しない — WKWebView では navigator.onLine が実態とずれたまま offline
    // イベントだけ飛ぶことがあり、OS が「接続中」と言っているのを上書きして
    // 帯が出っぱなしになる（2026-09-20 に Simulator で発生）。
    // Web ではプラグインの web 実装が online/offline を見るため挙動は変わらない。
    const refreshStatus = () => {
      isNetworkConnected().then((connected) => {
        if (!cancelled) setIsOnline(connected);
      });
    };
    refreshStatus();
    const unsubscribeNetwork = subscribeNetworkStatus((connected) => {
      if (!cancelled) setIsOnline(connected);
    });

    // 非表示の間に来た変化を取りこぼすため、再び可視になったら取り直す
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshStatus();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      unsubscribeNetwork();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      getPendingAnswerCount().then((count) => {
        if (!cancelled) setPendingCount(count);
      });
    };
    refresh();
    const interval = setInterval(refresh, PENDING_COUNT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOnline]);

  if (isOnline && pendingCount === 0) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-2 text-xs text-amber-700 dark:text-amber-400 flex items-center justify-center gap-2 flex-wrap">
      {!isOnline && (
        <span className="flex items-center gap-1.5">
          <WifiOff size={14} />
          オフラインです。保存済みの区分のみ出題できます
        </span>
      )}
      {pendingCount > 0 && <span>未送信の回答 {pendingCount} 件</span>}
    </div>
  );
};
